import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Download } from 'lucide-react';
import Sidebar from '../../components/Sidebar';
import Navbar from '../../components/Navbar';
import api, { BACKEND_URL } from '../../lib/api';

const TrialBalance = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  useEffect(() => {
    const t = localStorage.getItem('token'); const u = localStorage.getItem('user');
    if (!t || !u) { navigate('/login'); return; }
    setUser(JSON.parse(u));
    fetchTB();
  }, [navigate]);

  const fetchTB = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (startDate) params.set('start_date', startDate);
      if (endDate) params.set('end_date', endDate);
      const r = await api.get(`/accounts/trial-balance?${params}`);
      setData(r.data);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  const exportPDF = () => {
    const params = new URLSearchParams();
    if (startDate) params.set('start_date', startDate);
    if (endDate) params.set('end_date', endDate);
    const token = localStorage.getItem('token');
    window.open(`${BACKEND_URL}/api/accounts/export/trial-balance?${params}&token=${token}`, '_blank');
  };

  return (
    <div className="flex h-screen bg-[#f0f2f5]" data-testid="trial-balance-page">
      <Sidebar isOpen={isSidebarOpen} toggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)} />
      <div className={`flex-1 flex flex-col transition-all duration-300 ${isSidebarOpen ? 'md:ml-64' : 'md:ml-0'}`}>
        <Navbar user={user} toggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)} />
        <main className="flex-1 overflow-y-auto p-6 sm:p-8">
          <div className="mb-6 flex justify-between items-center">
            <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight text-[#1a1a2e]" data-testid="tb-title">Trial Balance</h1>
            {data && data.rows.length > 0 && <button onClick={exportPDF} className="flex items-center gap-1 text-[#1E40AF] font-medium hover:underline" data-testid="export-tb-pdf"><Download className="w-4 h-4" /> Export PDF</button>}
          </div>

          <div className="bg-white border border-[#e5e7eb] rounded-2xl shadow-sm p-4 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
              <div><label className="block text-sm font-medium mb-1">Start Date</label><input type="date" className="w-full border border-[#e5e7eb] rounded-sm px-3 py-2" value={startDate} onChange={e => setStartDate(e.target.value)} /></div>
              <div><label className="block text-sm font-medium mb-1">End Date</label><input type="date" className="w-full border border-[#e5e7eb] rounded-sm px-3 py-2" value={endDate} onChange={e => setEndDate(e.target.value)} /></div>
              <button onClick={fetchTB} className="bg-gradient-to-r from-[#22B8D4] via-[#4A7AE0] to-[#7C3BD6] text-white hover:opacity-90 rounded-sm px-4 py-2 font-medium" data-testid="fetch-tb-button">Refresh</button>
            </div>
          </div>

          {loading ? <div className="text-center py-12">Loading...</div> : !data || data.rows.length === 0 ? (
            <div className="bg-white border border-[#e5e7eb] rounded-2xl shadow-sm p-12 text-center text-[#6b7280]">No transactions found for this period</div>
          ) : (
            <div className="bg-white border border-[#e5e7eb] rounded-2xl shadow-sm overflow-x-auto">
              <table className="w-full text-sm" data-testid="tb-table">
                <thead className="bg-[#f9fafb] text-[#6b7280] font-medium border-b border-[#e5e7eb]">
                  <tr><th className="py-3 px-4 text-left">Code</th><th className="py-3 px-4 text-left">Account Name</th><th className="py-3 px-4 text-left">Type</th><th className="py-3 px-4 text-right">Debit (Rs.)</th><th className="py-3 px-4 text-right">Credit (Rs.)</th></tr>
                </thead>
                <tbody>
                  {data.rows.map((r, i) => (
                    <tr key={i} className="border-b border-[#e5e7eb] hover:bg-[#f9fafb]">
                      <td className="py-2 px-4 font-mono">{r.account_code}</td>
                      <td className="py-2 px-4">{r.account_name}</td>
                      <td className="py-2 px-4 capitalize text-[#6b7280]">{r.account_type}</td>
                      <td className="py-2 px-4 text-right font-medium">{r.debit > 0 ? r.debit.toLocaleString(undefined, { minimumFractionDigits: 2 }) : '-'}</td>
                      <td className="py-2 px-4 text-right font-medium">{r.credit > 0 ? r.credit.toLocaleString(undefined, { minimumFractionDigits: 2 }) : '-'}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-[#1E40AF] text-white font-bold">
                  <tr>
                    <td colSpan="3" className="py-3 px-4">TOTAL</td>
                    <td className="py-3 px-4 text-right">{data.total_debit.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                    <td className="py-3 px-4 text-right">{data.total_credit.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default TrialBalance;
